/**
 * SlideshowManage
 *
 * Runs the kiosk slideshow. It builds a list of slides
 * (intro → announcement → map → stations in selected languages), and then
 * auto-plays them with a progress bar. Users can pause, skip, and go fullscreen.
 *
 * summary:
 * - Builds `slides` from `config` and selected languages
 * - Uses time-based progress to avoid jitter on slow devices
 * - Supports auto-hide controls and auto-resume after inactivity
 * - Handles browser fullscreen with event listeners
 */
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Box, IconButton, LinearProgress, Fade, Button } from "@mui/material";
import {
  PlayArrow,
  Pause,
  SkipNext,
  SkipPrevious,
  Fullscreen,
  FullscreenExit,
} from "@mui/icons-material";
import IntroSlider from "./IntroSlider";
import AnnouncementSlider from "./AnnouncementSlider";
import MapSlider from "./MapSlider";
import WindBarbMapSlider from "./WindBarbMapSlider";
import StationSlide from "./StationSlides";
import SlideWrapper from "./SlideWrapper";
import { getSlideDuration } from "./slideConfig";
import {
  AUTO_RESUME_ENABLED,
  AUTO_RESUME_TIMEOUT,
  HIDE_CURSOR_TIMEOUT,
} from "./slideConfig";
import { useNavigate } from "react-router-dom";
import { languageService } from "../../../../context/languageService";

export default function SlideshowManage({ config }) {
  // State management
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showFullscreenPrompt, setShowFullscreenPrompt] = useState(true); // Show prompt initially
  const containerRef = useRef(null);
  const [showControls, setShowControls] = useState(true);
  const [hideCountdown, setHideCountdown] = useState(10);
  const hideTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const [hideCursor, setHideCursor] = useState(false);
  const autoResumeTimerRef = useRef(null);
  const progressRef = useRef(0); // Add ref to track progress without state batching issues
  const slideStartTimeRef = useRef(null); // Monotonic start time for current slide
  const pausedElapsedRef = useRef(0); // Elapsed time at the moment of pause
  const navigate = useNavigate();

  // Get current language preference (for reference only)
  const currentLanguage = languageService.getPreferredLanguage();

  // Get stations data and create slides for each enabled language
  const allStations = (config?.config?.stations || [])
    .slice()
    .sort((a, b) => a.order - b.order);

  // Create station slides for each enabled language
  // Build station slides for each station and language enabled
  const stationSlides = allStations.flatMap((station, originalIndex) => {
    const stationLanguages = station.languages || {
      english: true,
      inuktitut: true,
    };
    const slides = [];

    // Add slide for English if enabled
    if (stationLanguages.english === true) {
      slides.push({
        type: "station",
        stationIndex: originalIndex,
        language: "english",
        filteredIndex: slides.length,
      });
    }

    // Add slide for Inuktitut if enabled
    if (stationLanguages.inuktitut === true) {
      slides.push({
        type: "station",
        stationIndex: originalIndex,
        language: "inuktitut",
        filteredIndex: slides.length,
      });
    }

    return slides;
  });

  // Slides configuration
  const introImages = config?.introImages || [];
  const hasAnnouncementImage = !!config?.imageUrl;

  // Check if map should be shown based on configuration
  const mapConfig = config?.config?.map;
  const hasMapConfig =
    mapConfig && mapConfig.center && mapConfig.zoom && mapConfig.bounds;
  const mapLanguages = mapConfig?.languages || {
    english: true,
    inuktitut: true,
  };

  // Create map slides for each enabled language (only if map is configured)
  const mapSlides = [];
  if (hasMapConfig) {
    if (mapLanguages.english === true) {
      mapSlides.push({ type: "map", language: "english" });
    }
    if (mapLanguages.inuktitut === true) {
      mapSlides.push({ type: "map", language: "inuktitut" });
    }
  }

  // Check if wind barb map should be shown based on configuration
  const windBarbMapConfig = config?.config?.windBarbMap;
  const windBarbMapLanguages = windBarbMapConfig?.languages || {
    english: true,
    inuktitut: true,
  };
  const showWindBarbMap = windBarbMapConfig?.enabled || false;

  // Create wind barb map slides for each enabled language (only if map is configured and wind barb is enabled)
  const windBarbMapSlides = [];
  if (hasMapConfig && showWindBarbMap) {
    if (windBarbMapLanguages.english === true) {
      windBarbMapSlides.push({ type: "windBarbMap", language: "english" });
    }
    if (windBarbMapLanguages.inuktitut === true) {
      windBarbMapSlides.push({ type: "windBarbMap", language: "inuktitut" });
    }
  }

  // Memoize the slide list to avoid re-creating on every render
  const slides = useMemo(() => {
    const slideList = [
      ...introImages.map((img, idx) => ({
        type: "intro",
        introImage: img,
        introIndex: idx,
      })),
      ...(hasAnnouncementImage ? [{ type: "announcement" }] : []),
      ...mapSlides,
      ...windBarbMapSlides,
      ...stationSlides,
    ];

    // Debug logging
    console.log(
      `Language: ${currentLanguage}, Total slides: ${slideList.length}`
    );
    console.log(`Map slides included: ${mapSlides.length}`);
    console.log(`Wind Barb Map slides included: ${windBarbMapSlides.length}`);
    console.log(
      `Station slides included: ${stationSlides.length} (from ${allStations.length} stations)`
    );

    return slideList;
  }, [
    introImages,
    hasAnnouncementImage,
    mapSlides,
    windBarbMapSlides,
    stationSlides,
    currentLanguage,
    allStations.length,
  ]);

  // Guard: If current index is out of range after slides update, reset to 0
  useEffect(() => {
    if (currentSlide >= slides.length && slides.length > 0) {
      setCurrentSlide(0);
    }
  }, [slides.length, currentSlide]);

  const handleSettingsClick = () => {
    const communityId = config?.communityId || "communityid";
    navigate(`/kiosk/${communityId}/setting`);
  };

  // Get slide duration for the current slide type (stations can have custom timers)
  const getCurrentSlideDuration = useCallback(() => {
    const slide = slides[currentSlide];
    if (slide.type === "station") {
      const timer = allStations[slide.stationIndex]?.timer;
      return getSlideDuration("station", timer);
    }
    return getSlideDuration(slide.type);
  }, [currentSlide, slides, allStations]);

  // Progress tracking
  // Uses monotonic time so the bar never jumps backward (helps on slow TVs)
  useEffect(() => {
    if (!isPlaying) return;

    const duration = getCurrentSlideDuration();
    const interval = 100; // Update ~10x per second for smoothness

    const timer = setInterval(() => {
      // Initialize the start time on first tick
      const startTime =
        slideStartTimeRef.current != null
          ? slideStartTimeRef.current
          : performance.now();
      const elapsedMs = performance.now() - startTime;

      if (elapsedMs >= duration) {
        setCurrentSlide((i) => (i + 1) % slides.length);
        slideStartTimeRef.current = performance.now();
        pausedElapsedRef.current = 0;
        progressRef.current = 0;
        setProgress(0);
        return;
      }

      let computedProgress = (elapsedMs / duration) * 100;
      // Ensure progress only moves forward, even if timers jitter
      if (computedProgress < progressRef.current) {
        computedProgress = progressRef.current;
      }

      progressRef.current = computedProgress;
      setProgress(computedProgress);
    }, interval);

    return () => clearInterval(timer);
  }, [isPlaying, getCurrentSlideDuration, slides.length]);

  // Fullscreen toggle using the standard Fullscreen API
  const toggleFullscreen = async () => {
    try {
      if (!isFullscreen) {
        if (containerRef.current) {
          await containerRef.current.requestFullscreen();
        }
      } else {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        }
      }
    } catch (error) {
      console.error("Fullscreen error:", error);
    }
  };

  // Listen for fullscreen changes so UI can reflect the current state
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isNowFullscreen);

      // Show prompt again when exiting fullscreen
      if (!isNowFullscreen) {
        setShowFullscreenPrompt(true);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Handle fullscreen button click from overlay
  const handleEnterFullscreen = async () => {
    try {
      if (containerRef.current && !document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
        setShowFullscreenPrompt(false); // Hide the prompt after entering fullscreen
      }
    } catch (error) {
      console.error("Fullscreen error:", error);
      // If fullscreen fails, still hide the prompt and let slideshow continue
      setShowFullscreenPrompt(false);
    }
  };

  // Navigation functions
  const nextSlide = () => {
    setCurrentSlide((i) => (i + 1) % slides.length);
  };

  const previousSlide = () => {
    setCurrentSlide((i) => (i - 1 + slides.length) % slides.length);
  };

  // Pause/resume preserves elapsed time so slides resume where they left off
  const togglePlayPause = () => {
    setIsPlaying((wasPlaying) => {
      const willPlay = !wasPlaying;
      if (willPlay) {
        // Resume: subtract paused time from now
        const pausedElapsed = pausedElapsedRef.current || 0;
        slideStartTimeRef.current = performance.now() - pausedElapsed;
        pausedElapsedRef.current = 0;
      } else {
        // Pause: remember elapsed time so we can resume accurately
        const now = performance.now();
        const startTime = slideStartTimeRef.current || now;
        pausedElapsedRef.current = now - startTime;
      }
      return willPlay;
    });
  };

  // Auto-resume after inactivity (if enabled)
  const startAutoResumeTimer = useCallback(() => {
    if (autoResumeTimerRef.current) {
      clearTimeout(autoResumeTimerRef.current);
    }
    if (AUTO_RESUME_ENABLED) {
      autoResumeTimerRef.current = setTimeout(() => {
        setIsPlaying(true);
      }, AUTO_RESUME_TIMEOUT);
    }
  }, []);

  // Show controls on any user interaction and reset hide timers/counters
  const handleUserInteraction = useCallback(() => {
    setShowControls(true);
    setHideCursor(false);
    setHideCountdown(10);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (countdownIntervalRef.current)
      clearInterval(countdownIntervalRef.current);
    // If paused, (re)start the auto-resume timer; otherwise cancel it
    if (!isPlaying) {
      startAutoResumeTimer();
    } else if (autoResumeTimerRef.current) {
      clearTimeout(autoResumeTimerRef.current);
      autoResumeTimerRef.current = null;
    }
    // Start countdown label updater (purely visual)
    countdownIntervalRef.current = setInterval(() => {
      setHideCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    // Hide controls after timeout
    hideTimerRef.current = setTimeout(() => {
      setShowControls(false);
      setHideCountdown(10);
      clearInterval(countdownIntervalRef.current);
    }, HIDE_CURSOR_TIMEOUT);
    // Also hide cursor after the same timeout
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setShowControls(false);
      setHideCursor(true);
      setHideCountdown(10);
      clearInterval(countdownIntervalRef.current);
    }, HIDE_CURSOR_TIMEOUT);
  }, [isPlaying, startAutoResumeTimer]);

  // Attach interaction listeners
  useEffect(() => {
    const events = ["mousemove", "mousedown", "touchstart", "keydown", "click"];
    events.forEach((event) => {
      window.addEventListener(event, handleUserInteraction);
    });
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleUserInteraction);
      });
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (countdownIntervalRef.current)
        clearInterval(countdownIntervalRef.current);
    };
  }, [handleUserInteraction]);

  // Simple auto-resume effect
  useEffect(() => {
    if (!isPlaying) {
      startAutoResumeTimer();
    } else if (autoResumeTimerRef.current) {
      clearTimeout(autoResumeTimerRef.current);
      autoResumeTimerRef.current = null;
    }
    return () => {
      if (autoResumeTimerRef.current) {
        clearTimeout(autoResumeTimerRef.current);
        autoResumeTimerRef.current = null;
      }
    };
  }, [isPlaying, startAutoResumeTimer]);

  // Start initial hide timer on mount
  useEffect(() => {
    handleUserInteraction();
    // eslint-disable-next-line
  }, []);

  // Reset timing when the current slide changes
  useEffect(() => {
    slideStartTimeRef.current = performance.now();
    pausedElapsedRef.current = 0;
    progressRef.current = 0;
    setProgress(0);
  }, [currentSlide]);

  // Render current slide
  const renderSlide = () => {
    const slide = slides[currentSlide];

    switch (slide.type) {
      case "intro":
        return (
          <SlideWrapper slideType="intro">
            <IntroSlider image={slide.introImage} />
          </SlideWrapper>
        );
      case "announcement":
        return (
          <SlideWrapper slideType="announcement">
            <AnnouncementSlider config={config} />
          </SlideWrapper>
        );
      case "map":
        return (
          <SlideWrapper slideType="map">
            <MapSlider config={config} language={slide.language} />
          </SlideWrapper>
        );
      case "windBarbMap":
        return (
          <SlideWrapper slideType="windBarbMap">
            <WindBarbMapSlider config={config} language={slide.language} />
          </SlideWrapper>
        );
      case "station":
        return (
          <SlideWrapper slideType="station">
            <StationSlide
              config={config}
              stationIndex={slide.stationIndex}
              language={slide.language}
              isFullscreen={isFullscreen}
              fullscreenContainerRef={containerRef}
            />
          </SlideWrapper>
        );
      default:
        return null;
    }
  };

  // Container styles
  const containerStyles = {
    width: "100%",
    height: "100vh",
    // margin: "5vh auto",
    position: "relative",
    backgroundColor: "background.default",
    borderRadius: 3,
    overflow: "hidden",
    boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
    border: "1px solid",
    borderColor: "divider",
    transition: "all 0.3s ease",
  };

  // Fullscreen styles
  const fullscreenStyles = {
    width: "100vw",
    height: "100vh",
    margin: 0,
    borderRadius: 0,
    boxShadow: "none",
    border: "none",
    overflow: "hidden",
  };

  return (
    <>
      {/* Slideshow Window */}
      <Box
        ref={containerRef}
        sx={{
          ...(isFullscreen ? fullscreenStyles : containerStyles),
          cursor: hideCursor ? "none" : "default",
          // Hide scrollbars
          "&::-webkit-scrollbar": {
            display: "none",
          },
          scrollbarWidth: "none", // Firefox
          msOverflowStyle: "none", // IE/Edge
        }}
      >
        {/* Main Content */}
        <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
          <Fade in={true} timeout={300}>
            <Box sx={{ width: "100%", height: "100%" }}>{renderSlide()}</Box>
          </Fade>
        </Box>

        {/* Fullscreen Prompt Overlay */}
        {showFullscreenPrompt && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(15px)",
              WebkitBackdropFilter: "blur(15px)", // Safari support
              zIndex: 1050, // Above controls (9999) but below navbar (1100)
              animation: "fadeIn 0.3s ease-in",
              "@keyframes fadeIn": {
                from: { opacity: 0 },
                to: { opacity: 1 },
              },
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2.5,
                padding: 5,
                maxWidth: 600,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                borderRadius: 4,
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              }}
            >
              {/* Icon */}
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  backgroundColor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                  animation: "pulse 2s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { transform: "scale(1)" },
                    "50%": { transform: "scale(1.05)" },
                  },
                }}
              >
                <Fullscreen
                  sx={{
                    fontSize: 50,
                    color: "white",
                  }}
                />
              </Box>

              {/* Title */}
              <Box
                sx={{
                  color: "text.primary",
                  fontSize: "2rem",
                  fontWeight: 600,
                  mt: 1,
                }}
              >
                Kiosk Mode
              </Box>

              {/* Description */}
              <Box
                sx={{
                  color: "text.secondary",
                  fontSize: "1.05rem",
                  lineHeight: 1.6,
                  maxWidth: 500,
                  textAlign: "center",
                }}
              >
                For the best viewing experience, enter fullscreen mode to
                display the slideshow without distractions
              </Box>

              {/* Fullscreen Button */}
              <Button
                onClick={handleEnterFullscreen}
                variant="contained"
                size="large"
                startIcon={<Fullscreen sx={{ fontSize: 28 }} />}
                sx={{
                  mt: 2,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  px: 5,
                  py: 2,
                  borderRadius: 2,
                  backgroundColor: "primary.main",
                  color: "white",
                  textTransform: "none",
                  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.2)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                Click Here to Enter Fullscreen
              </Button>

              {/* Skip button (optional) */}
              <Button
                onClick={() => setShowFullscreenPrompt(false)}
                sx={{
                  color: "text.secondary",
                  fontSize: "0.9rem",
                  textTransform: "none",
                  textDecoration: "underline",
                  "&:hover": {
                    color: "text.primary",
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                Continue without fullscreen
              </Button>
            </Box>
          </Box>
        )}

        {/* Progress Bar */}
        <Box
          sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 1000 }}
        >
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 3,
              backgroundColor: "rgba(255,255,255,0.2)",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "primary.main",
              },
            }}
          />
        </Box>
        {/* Navigation Controls, Fullscreen, Slide Counter */}
        {showControls && !hideCursor && !showFullscreenPrompt && (
          <>
            {/* Navigation Controls + Slide Counter */}
            <Box
              sx={{
                position: "absolute",
                bottom: 32,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                gap: 2,
                backgroundColor: "rgba(0,0,0,0.7)",
                borderRadius: 3,
                padding: 2,
                backdropFilter: "blur(8px)",
                boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
                minWidth: 340,
              }}
            >
              {/* Previous */}
              <Button
                onClick={previousSlide}
                startIcon={<SkipPrevious sx={{ fontSize: 36 }} />}
                sx={{
                  color: "white",
                  border: "1.5px solid #fff",
                  borderRadius: 3,
                  fontSize: "1rem",
                  fontWeight: 500,
                  px: 2,
                  py: 1.2,
                  backgroundColor: "transparent",
                  minWidth: 120,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  boxShadow: "none",
                }}
                size="large"
              >
                Previous
              </Button>

              {/* Play/Pause */}
              <Button
                onClick={togglePlayPause}
                startIcon={
                  isPlaying ? (
                    <Pause sx={{ fontSize: 36 }} />
                  ) : (
                    <PlayArrow sx={{ fontSize: 36 }} />
                  )
                }
                sx={{
                  color: "white",
                  border: "1.5px solid #fff",
                  borderRadius: 3,
                  fontSize: "1rem",
                  fontWeight: 500,
                  px: 2,
                  py: 1.2,
                  backgroundColor: "transparent",
                  minWidth: 120,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  boxShadow: "none",
                }}
                size="large"
              >
                {isPlaying ? "Pause" : "Play"}
              </Button>

              {/* Next */}
              <Button
                onClick={nextSlide}
                startIcon={<SkipNext sx={{ fontSize: 36 }} />}
                sx={{
                  color: "white",
                  border: "1.5px solid #fff",
                  borderRadius: 3,
                  fontSize: "1rem",
                  fontWeight: 500,
                  px: 2,
                  py: 1.2,
                  backgroundColor: "transparent",
                  minWidth: 120,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  boxShadow: "none",
                }}
                size="large"
              >
                Next
              </Button>

              {/* Slide Counter (right after icons) */}
              <Box
                component="span"
                sx={{
                  color: "white",
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  ml: 2,
                  minWidth: 60,
                  textAlign: "center",
                  letterSpacing: 1,
                }}
              >
                {(() => {
                  const slide = slides[currentSlide];
                  const language = slide?.language;
                  const languageText =
                    language === "english"
                      ? "English"
                      : language === "inuktitut"
                      ? "Inuktitut"
                      : "";
                  return `${currentSlide + 1} / ${slides.length}${
                    languageText ? ` (${languageText})` : ""
                  }`;
                })()}
              </Box>
            </Box>

            {/* Fullscreen Toggle */}
            <Box
              sx={{
                position: "absolute",
                top: 32,
                right: 32,
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                gap: 1,

                backgroundColor: "rgba(0,0,0,0.7)",
                borderRadius: 3,
                padding: 1,
              }}
            >
              <Button
                onClick={toggleFullscreen}
                startIcon={
                  isFullscreen ? (
                    <FullscreenExit sx={{ fontSize: 32 }} />
                  ) : (
                    <Fullscreen sx={{ fontSize: 32 }} />
                  )
                }
                sx={{
                  color: "white",
                  border: "1.5px solid #fff",
                  borderRadius: 3,
                  fontSize: "1rem",
                  fontWeight: 500,
                  px: 2,
                  py: 1.2,
                  backgroundColor: "transparent",
                  minWidth: 120,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  boxShadow: "none",
                }}
                size="large"
              >
                {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              </Button>
            </Box>
          </>
        )}
      </Box>
      {/* Settings Button Section - below slideshow */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: 4,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 600, px: 2 }}>
          <Box sx={{ borderTop: "1px solid", borderColor: "divider", mb: 3 }} />
        </Box>
        <Box
          sx={{
            mb: 2,
            maxWidth: 700,
            textAlign: "center",
            color: "text.secondary",
            fontSize: "1.05rem",
          }}
        >
          If you would like to change the kiosk configuration, you can edit the
          settings below.
        </Box>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleSettingsClick}
          sx={{
            fontWeight: 600,
            fontSize: "1rem",
            px: 4,
            py: 1.5,
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            backgroundColor: "background.paper",
            transition: "background 0.2s",
            "&:hover": { backgroundColor: "primary.light", color: "white" },
          }}
          size="large"
        >
          <span role="img" aria-label="settings" style={{ marginRight: 8 }}>
            ⚙️
          </span>
          Edit Kiosk Settings
        </Button>
        <Box sx={{ height: 24 }} />
      </Box>
    </>
  );
}
